<script setup lang="ts">
import { computed, defineEmits, onMounted, ref } from 'vue'
import { NFormItemGi, NGrid } from 'naive-ui'
import { getDetailWorkSpace } from '@/api/workspace'
import { useUserStore } from '@/store'
import { renderColor } from '@/composables/useRenderColor'
import { t } from '@/locales'
import { GLOBAL_LIST } from '@/constants'
import { AppSvgEIcon } from '@/components/common'
import { getGlobalListDetail } from '@/api/parameterConfiguration'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['onClose'])

const userStore = useUserStore()
const workSpaceId = computed(() => userStore.workSpaceId)

const showModal = ref<boolean>(props.show)
const modelRef = ref<any> ({
  id: null,
  name: null,
  industry: [],
  description: '',
  district_id: null,
  district_name: null,
  district_code: null,
  province_id: null,
  province_name: null,
  province_code: null,
  address: null,
  educational_level: null,
  website: null,
  avatar_id: null,
  avatar_url: null,
  grading_scale: null,
  sdt: null,
  status: null,
  type: null,
  user_email: null,
  user_phone: null,
})

const listGradingScale = ref<any[]>([])
const listEducationalLevel = ref<any[]>([])

const closeModal = (reload: boolean) => {
  showModal.value = false
  emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const fetchDetailWorkSpace = async () => {
  if (!workSpaceId.value)
    return
  try {
    const res = await getDetailWorkSpace({ workspace_id: workSpaceId.value })
    if (res.success)
      modelRef.value = { ...res.data.workspace }
  }
  catch {}
}
const fetchEducationLevels = async () => {
  try {
    const params = {
      global_list_code: GLOBAL_LIST.EDUCATIONAL_LEVEL,
    }
    const res = await getGlobalListDetail(params)
    if (res.success)
      listEducationalLevel.value = res.data.items || []
  }
  catch (e) {
  }
}
const getListGradingScale = async () => {
  try {
    const params = {
      global_list_code: GLOBAL_LIST.GRADING_SCALE,
    }
    const res = await getGlobalListDetail(params)
    if (res.success)
      listGradingScale.value = res.data.items || []
  }
  catch (e) {
  }
}

onMounted(() => {
  fetchDetailWorkSpace()
  fetchEducationLevels()
  getListGradingScale()
})
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    class="!rounded-3xl xl:max-w-[1000px] max-w-[800px] w-full max-h-[95vh] !py-6 relative"
    content-class="!py-0 px-6 !h-full overflow-auto "
    header-class="!p-4 text-center"
    footer-class="!p-4 !w-full"
    @after-leave="handleCancel"
    @esc="handleCancel"
    @mask-click="handleCancel"
  >
    <div>
      <div>
        <div class="space-y-4">
          <div class="flex items-center gap-4">
            <div
              class="w-10 h-10 flex items-center justify-center rounded-lg" :style="{
                backgroundColor: renderColor(0.1),
              }"
            >
              <SvgIcon icon="streamline-flex:school" class="text-primary" size="24" />
            </div>
            <div class="text-lg font-bold">
              {{ $t('settings.workspaceInfoTab.title') }}
            </div>
          </div>
          <div class="flex items-start gap-4 max-[840px]:flex-col ">
            <div
              class="w-36 h-36 flex flex-col items-center justify-center border border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-primary transition-colors max-[840px]:mx-auto"
            >
              <img
                v-if="modelRef.avatar_url"
                :src="modelRef.avatar_url"
                alt="Avatar"
                class="w-full h-full object-cover rounded-lg"
              >
              <div v-else class="flex flex-col items-center justify-center h-full">
                <AppSvgEIcon name="gallery" :size="24" />
                <div class="text-sm mt-2">
                  {{ $t('settings.workspaceInfoTab.urlDescription') }}
                </div>
              </div>
            </div>
            <div class="flex-1 max-[840px]:w-full">
              <NGrid :cols="3" :x-gap="16">
                <NFormItemGi :label="$t('settings.workspaceInfoTab.workspaceName')" path="name">
                  <AppInput
                    v-model:value="modelRef.name" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('auth.province')" path="province_id">
                  <AppInput
                    v-model:value="modelRef.province_name" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('auth.district')" path="district_id">
                  <AppInput
                    v-model:value="modelRef.district_name" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
              </NGrid>
              <NGrid :cols="1" :x-gap="16">
                <NFormItemGi :label="$t('settings.workspaceInfoTab.address')" path="address">
                  <AppInput
                    v-model:value="modelRef.address" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
              </NGrid>
              <NGrid :cols="3" :x-gap="16">
                <NFormItemGi :label="$t('settings.workspaceInfoTab.educationalLevel')" path="educational_level">
                  <AppSelect
                    v-model:value="modelRef.educational_level"
                    :options="listEducationalLevel"
                    placeholder=""
                    label-field="name"
                    value-field="code"
                    size="medium"
                    class=""
                    filterable
                    clearable
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('settings.workspaceInfoTab.website')" path="website">
                  <AppInput
                    v-model:value="modelRef.website" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('settings.workspaceInfoTab.gradingScale')" path="grading_scale">
                  <AppSelect
                    v-model:value="modelRef.grading_scale"
                    :options="listGradingScale"
                    placeholder=""
                    label-field="name"
                    value-field="code"
                    size="medium"
                    class=""
                    filterable
                    clearable
                    :disabled="true"
                  />
                </NFormItemGi>
              </NGrid>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4">
        <div class="space-y-4">
          <div class="flex items-center gap-4 justify-between">
            <div class="flex items-center gap-4">
              <div
                class="w-10 h-10 flex items-center justify-center rounded-lg text-primary" :style="{
                  backgroundColor: renderColor(0.1),
                }"
              >
                <AppSvgEIcon name="user-2" :size="24" />
              </div>
              <div class="text-lg font-bold">
                {{ $t('settings.workspaceInfoTab.adminInfoTitle') }}
              </div>
            </div>
            <div v-if="isEdit">
              <AppButton text size="medium" type="primary" class="" @click="handleChangeAdmin">
                {{ $t('settings.workspaceInfoTab.changeAdmin') }}
              </AppButton>
            </div>
          </div>
          <div class="flex items-start gap-4 max-[840px]:flex-col ">
            <div class="flex-1 max-[840px]:w-full">
              <NGrid :cols="3" :x-gap="16">
                <NFormItemGi :label="$t('auth.full_name')" path="user_name">
                  <AppInput
                    v-model:value="modelRef.user_name" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('auth.email')" path="owner">
                  <AppInput
                    v-model:value="modelRef.owner" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
                <NFormItemGi :label="t('auth.phone')" path="sdt">
                  <AppInput
                    v-model:value="modelRef.sdt" size="medium" clearable
                    placeholder=""
                    :disabled="true"
                  />
                </NFormItemGi>
              </NGrid>
              <NGrid :cols="3" :x-gap="16">
                <NFormItemGi :label="t('settings.workspaceInfoTab.userRole')" path="user_role">
                  <AppInput
                    v-model:value="modelRef.user_role" size="medium" clearable
                    :placeholder="$t('settings.workspaceInfoTab.userRolePlaceholder')"
                    :disabled="!isEdit"
                  />
                </NFormItemGi>
              </NGrid>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <AppButton size="medium" type="primary" class=" min-w-32" @click="handleCancel">
        {{ $t('common.close') }}
      </AppButton>
    </div>
  </AppModal>
</template>

<style scoped>

</style>
